<script setup lang="ts">
import CircleIcon from "@/components/shared/CircleIcon.vue"
import {
  Dialog,
  DialogOverlay,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue"
import { XIcon } from "@heroicons/vue/outline"
import Border from "@/components/shared/Border.vue"

defineProps<{
  title: string
  icon: InstanceType<(typeof CircleIcon)["$props"]>
}>()

const showModal = defineModel<boolean>({ required: true })
</script>

<template>
  <TransitionRoot as="template" :show="showModal">
    <Dialog
      as="div"
      static
      class="fixed z-30 inset-0 overflow-y-auto antialiased text-xy-black"
      :open="showModal"
      @close="showModal = false"
    >
      <div
        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span
          class="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
          >&#8203;</span
        >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="inline-block align-bottom bg-white rounded-xy text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl w-full"
          >
            <div class="block absolute z-10 top-0 right-0 pt-4 pr-4">
              <button
                type="button"
                class="group rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                @click="showModal = false"
              >
                <span class="sr-only">Close</span>
                <CircleIcon
                  :icon="XIcon"
                  bg-color="bg-neutral-50 group-hover:bg-neutral-100"
                  icon-color="text-neutral-500 group-hover:text-neutral-700"
                  size="small"
                />
              </button>
            </div>

            <div class="bg-white p-5 pb-5 sm:p-8 sm:pb-6">
              <div
                class="flex items-center pr-5 space-x-2 md:space-x-3.5 text-xy-black"
              >
                <div class="shrink-0" aria-hidden="true">
                  <CircleIcon v-bind="icon" size="normal" />
                </div>
                <div>
                  <DialogTitle
                    as="h3"
                    class="font-display font-extrabold text-xl leading-6 sm:text-2xl sm:leading-7"
                  >
                    {{ title }}
                  </DialogTitle>
                </div>
              </div>
              <Border size="short" class="ml-2 mt-5 mb-6" />
              <div>
                <slot />
              </div>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
